import type { QueryClient } from '@tanstack/react-query'

export type TrainingApiTarget = 'legacy' | 'modern' | 'pending' | 'unsupported'
export type TrainingApiOptions<T extends TrainingApiTarget = TrainingApiTarget> = { target?: T }

export type TrainingModuleCondition = { type: string; values: string[] }
export type OntologyTrainingModule = {
  trainingModuleId: string
  programId: string
  title: string
  description: string | null
  mediaId: string | null
  mediaLocation: string | null
  pipelineJobId: string | null
  engagementCount: number | null
  isPublished: boolean
  trainingOrder: number | null
  dateRangeStart: string | null
  dateRangeEnd: string | null
  movingDayCount: number | null
  accuracyMin: number | null
  accuracyMax: number | null
  targetAccuracy: number | null
  targetResolution: number | null
  overallMaxAttempts: number | null
  dailyMaxAttempts: number | null
  conditionsWeight: number | null
  conditions: TrainingModuleCondition[]
  detailedFeedback: boolean
  purpose?: 'onboarding' | 'requalification'
  stats: {
    avgAttemptsToPass: number
    trendAttemptsToPass: number | null
    avgAccuracy: number | null
    trendAccuracy: number | null
    avgTimeToClose: number | null
    trendTimeToClose: number | null
  } | null
}

export type TrainingModuleFormInput = {
  title?: string
  description?: string
  program_id?: string
  media_id?: string | null
  pipeline_job_id?: string | null
  engagement_count?: number | null
  is_published?: boolean
  training_order?: number
  range_start_date?: string | null
  range_end_date?: string | null
  moving_day_count?: number | null
  accuracy_min?: number | null
  accuracy_max?: number | null
  target_accuracy?: number | null
  target_resolution?: number | null
  ovr_max_attempts?: number | null
  daily_max_attempts?: number | null
  conditions_weight?: number | null
  conditions?: TrainingModuleCondition[]
  detailed_feedback?: boolean
}

export type ModernTrainingModuleWrite = Partial<
  Pick<
    OntologyTrainingModule,
    | 'mediaId'
    | 'pipelineJobId'
    | 'engagementCount'
    | 'isPublished'
    | 'dateRangeStart'
    | 'dateRangeEnd'
    | 'movingDayCount'
    | 'accuracyMin'
    | 'accuracyMax'
    | 'targetAccuracy'
    | 'targetResolution'
    | 'overallMaxAttempts'
    | 'dailyMaxAttempts'
    | 'conditionsWeight'
    | 'conditions'
    | 'detailedFeedback'
  >
> & { title?: string; description?: string }

export type ModernLearnerModule = Pick<
  OntologyTrainingModule,
  | 'trainingModuleId'
  | 'title'
  | 'description'
  | 'mediaId'
  | 'mediaLocation'
  | 'engagementCount'
  | 'isPublished'
  | 'trainingOrder'
  | 'accuracyMin'
  | 'accuracyMax'
  | 'targetAccuracy'
  | 'targetResolution'
  | 'overallMaxAttempts'
  | 'dailyMaxAttempts'
> & {
  program: {
    id: string
    code: string
    displayName: string | null
    operatorDisplayName: string | null
    color: string | null
    iconId: string | null
  }
  isCompleted: boolean
  bestAccuracy: number | null
  bestSpeed: number | null
  specialistAttemptsToday: number
  specialistAttemptsOverall: number
}

export const TRAINING_QUERY_KEYS = {
  configuration: 'program-training-module-key',
  report: 'program-plan-capacity-ms',
  metrics: 'program-plan-capacity-metrics',
  learner: 'ontology-training-modules',
} as const

export function getTrainingApiTarget(apiVersion?: number): TrainingApiTarget {
  if (apiVersion === 2) return 'modern'
  if (apiVersion === 1) return 'legacy'
  return apiVersion === undefined ? 'pending' : 'unsupported'
}

export function trainingQueryKey(key: string, programId: string | undefined, target: TrainingApiTarget) {
  return target === 'legacy' ? [key, programId] : [key, programId, target]
}

export function trainingApiPrefix(target: TrainingApiTarget): '/int/v2' | '/int/v3' {
  if (target === 'pending') throw new Error('Training program metadata is not available')
  if (target === 'unsupported') throw new Error('Training is not supported for this program version')
  return target === 'modern' ? '/int/v3' : '/int/v2'
}

export function assertTrainingWriteScope(programId: string | undefined, target: TrainingApiTarget) {
  trainingApiPrefix(target)
  if (target === 'modern' && !programId) {
    throw new Error('Training program metadata is not available')
  }
}

export function mapTrainingModuleWrite(values: TrainingModuleFormInput): ModernTrainingModuleWrite {
  const body: ModernTrainingModuleWrite = {
    title: values.title,
    description: values.description,
    mediaId: values.media_id,
    pipelineJobId: values.pipeline_job_id,
    engagementCount: values.engagement_count,
    isPublished: values.is_published,
    dateRangeStart: values.range_start_date,
    dateRangeEnd: values.range_end_date,
    movingDayCount: values.moving_day_count,
    accuracyMin: values.accuracy_min,
    accuracyMax: values.accuracy_max,
    targetAccuracy: values.target_accuracy,
    targetResolution: values.target_resolution,
    overallMaxAttempts: values.ovr_max_attempts,
    dailyMaxAttempts: values.daily_max_attempts,
    conditionsWeight: values.conditions_weight,
    conditions: values.conditions,
    detailedFeedback: values.detailed_feedback,
  }
  return Object.fromEntries(Object.entries<unknown>(body).filter(([, value]) => value !== undefined))
}

export function invalidateTrainingModuleQueries(
  client: QueryClient,
  programId: string | undefined,
  target: TrainingApiTarget
) {
  void client.invalidateQueries({
    queryKey: trainingQueryKey(TRAINING_QUERY_KEYS.configuration, programId, target),
  })
  if (target !== 'modern') return
  for (const key of [TRAINING_QUERY_KEYS.report, TRAINING_QUERY_KEYS.metrics]) {
    void client.invalidateQueries({ queryKey: trainingQueryKey(key, programId, target) })
  }
  void client.invalidateQueries({ queryKey: [TRAINING_QUERY_KEYS.learner, programId] })
  for (const key of ['training-stats-mine', 'training-stats']) {
    void client.invalidateQueries({ queryKey: [key] })
  }
}
